import React, { useState } from 'react';
import { Upload, FileText, Image, Sparkles, Sliders, AlertCircle, Loader2 } from 'lucide-react';
import { EXAMS } from '../data/exams';
import { parseUploadedDocument, prepareTextChunks } from '../utils/docParser';

export default function NoteUploader({ onGenerate, isGenerating, selectedExam }) {
  const [activeTab, setActiveTab] = useState('upload'); // 'upload' | 'text'
  const [file, setFile] = useState(null);
  const [pastedText, setPastedText] = useState('');
  const [exam, setExam] = useState(selectedExam || 'UPSC CSE');
  const [itemCount, setItemCount] = useState(10);
  const [targetType, setTargetType] = useState('both'); // 'flashcards' | 'quiz' | 'both'
  const [difficulty, setDifficulty] = useState('Medium');
  
  const [parsingStatus, setParsingStatus] = useState('');
  const [error, setError] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);

  const handleFileDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
      setError('');
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    let textToProcess = '';
    
    if (activeTab === 'upload') {
      if (!file) {
        setError('Please select or drag a PDF, Slide, or handwritten image file to upload.');
        return;
      }

      try {
        setParsingStatus('Extracting content from uploaded file...');
        const pages = await parseUploadedDocument(file, (progress) => {
          setParsingStatus(progress.stage || 'Processing document...');
        });

        const chunks = prepareTextChunks(pages);
        textToProcess = chunks.map(c => c.text).join('\n\n');

        if (!textToProcess.trim()) {
          throw new Error('No readable text content found in file.');
        }
      } catch (err) {
        setParsingStatus('');
        setError(err.message || 'Failed to process document file.');
        return;
      }
    } else {
      if (!pastedText.trim() || pastedText.trim().length < 50) {
        setError('Please paste at least 50 characters of study notes text.');
        return;
      }
      textToProcess = pastedText.trim();
    }

    setParsingStatus('AI generating active-recall flashcards & practice test...');
    try {
      await onGenerate({
        notesText: textToProcess,
        exam,
        targetType,
        count: itemCount,
        difficulty,
        sourceFileName: file?.name || 'Pasted Notes',
      });
    } catch (err) {
      setError(err.message || 'AI Generation failed. Check API configuration or retry.');
    } finally {
      setParsingStatus('');
    }
  };

  return (
    <div className="bg-dark-700/50 border border-white/10 rounded-2xl p-6 mb-6 backdrop-blur-xl shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 pb-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-saffron-500/10 border border-saffron-500/20 flex items-center justify-center text-saffron-400 flex-shrink-0">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <h2 className="font-display text-xl sm:text-2xl font-bold text-white">
              AI Note-to-Flashcard & Quiz Generator
            </h2>
            <p className="text-xs sm:text-sm text-white/50 mt-0.5">
              Upload lecture slides, textbook PDFs, or handwritten notes to instantly generate active-recall decks.
            </p>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="flex bg-dark-600/70 p-1 rounded-xl border border-white/5 self-start sm:self-center">
          <button
            type="button"
            onClick={() => setActiveTab('upload')}
            className={`px-3.5 py-1.5 text-xs font-semibold rounded-lg transition-all ${
              activeTab === 'upload'
                ? 'bg-saffron-500/20 text-saffron-400 border border-saffron-500/30 shadow-sm'
                : 'text-white/50 hover:text-white'
            }`}
          >
            Upload File
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('text')}
            className={`px-3.5 py-1.5 text-xs font-semibold rounded-lg transition-all ${
              activeTab === 'text'
                ? 'bg-saffron-500/20 text-saffron-400 border border-saffron-500/30 shadow-sm'
                : 'text-white/50 hover:text-white'
            }`}
          >
            Paste Text
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Upload Zone */}
        {activeTab === 'upload' ? (
          <div
            onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={handleFileDrop}
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
              isDragOver
                ? 'border-saffron-500 bg-saffron-500/10 scale-[0.99]'
                : file
                ? 'border-emerald-500/50 bg-emerald-500/10'
                : 'border-white/10 hover:border-saffron-500/40 bg-dark-600/40'
            }`}
          >
            <input
              type="file"
              id="note-file-input"
              className="hidden"
              accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.md"
              onChange={handleFileSelect}
            />

            {file ? (
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-emerald-500/15 border border-emerald-500/30 rounded-full flex items-center justify-center text-emerald-400 mb-3">
                  <FileText className="w-6 h-6" />
                </div>
                <p className="text-sm font-semibold text-white">{file.name}</p>
                <p className="text-xs text-white/40 mt-1">{(file.size / (1024 * 1024)).toFixed(2)} MB</p>
                <button
                  type="button"
                  onClick={() => setFile(null)}
                  className="mt-3 text-xs font-medium text-rose-400 hover:text-rose-300 underline"
                >
                  Change File
                </button>
              </div>
            ) : (
              <label htmlFor="note-file-input" className="cursor-pointer flex flex-col items-center">
                <div className="w-12 h-12 bg-saffron-500/10 border border-saffron-500/20 text-saffron-400 rounded-full flex items-center justify-center mb-3">
                  <Upload className="w-6 h-6" />
                </div>
                <p className="text-sm font-semibold text-white/80">
                  Click to upload or drag & drop lecture slides or notes
                </p>
                <p className="text-xs text-white/40 mt-1.5 flex items-center justify-center gap-2 flex-wrap">
                  <span>PDF, PPT Slides, Plain Text</span>
                  <span>•</span>
                  <span className="flex items-center gap-1 text-saffron-400 font-medium">
                    <Image className="w-3.5 h-3.5" /> Handwritten Note OCR Supported
                  </span>
                </p>
              </label>
            )}
          </div>
        ) : (
          <div className="mb-4">
            <textarea
              rows={6}
              value={pastedText}
              onChange={(e) => setPastedText(e.target.value)}
              placeholder="Paste lecture notes, NCERT summaries, topic bullet points, or articles here..."
              className="input font-mono text-sm"
            />
          </div>
        )}

        {/* Options Panel */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mt-6 p-4 bg-dark-800/40 rounded-xl border border-white/5">
          <div>
            <label className="block text-xs font-semibold text-white/60 mb-1.5 flex items-center gap-1">
              <Sliders className="w-3.5 h-3.5 text-saffron-400" /> Target Exam
            </label>
            <select
              value={exam}
              onChange={(e) => setExam(e.target.value)}
              className="input text-xs py-2 px-3"
            >
              {EXAMS.map((e) => (
                <option key={e.id} value={e.name}>{e.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-white/60 mb-1.5">Output Type</label>
            <select
              value={targetType}
              onChange={(e) => setTargetType(e.target.value)}
              className="input text-xs py-2 px-3"
            >
              <option value="both">Both (Flashcards & Quiz)</option>
              <option value="flashcards">Flashcards Only</option>
              <option value="quiz">Practice Quiz Only</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-white/60 mb-1.5">Items Count</label>
            <select
              value={itemCount}
              onChange={(e) => setItemCount(Number(e.target.value))}
              className="input text-xs py-2 px-3"
            >
              <option value={5}>5 Items</option>
              <option value={10}>10 Items</option>
              <option value={15}>15 Items</option>
              <option value={20}>20 Items</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-white/60 mb-1.5">Difficulty Depth</label>
            <select
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value)}
              className="input text-xs py-2 px-3"
            >
              <option value="Easy">Easy (Factual / Recall)</option>
              <option value="Medium">Medium (Conceptual)</option>
              <option value="Hard">Hard (Traps & Applications)</option>
            </select>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 text-red-400 text-xs rounded-xl flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Progress Alert */}
        {parsingStatus && (
          <div className="mt-4 p-3 bg-saffron-500/10 border border-saffron-500/20 text-saffron-400 text-xs rounded-xl flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
            <span className="font-medium">{parsingStatus}</span>
          </div>
        )}

        {/* Generate Button */}
        <div className="mt-6 flex justify-end">
          <button
            type="submit"
            disabled={isGenerating || !!parsingStatus}
            className="btn-primary text-sm flex items-center gap-2 disabled:opacity-50"
          >
            {isGenerating || parsingStatus ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Generating Active-Recall Deck...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                Auto-Generate Deck & Practice Test
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
